import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Youtube,
  MessageCircle,
  Instagram,
  Send as Telegram,
  Facebook,
  Save,
  Loader2,
  Shield,
  Link as LinkIcon,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export default function SocialLinks() {
  const { isAdmin } = useAuth();
  const [links, setLinks] = useState({
    youtube: '',
    whatsapp: '',
    instagram: '',
    telegram: '',
    facebook: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const socialPlatforms = [
    { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@votrechaine', color: 'text-red-500' },
    { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/votrepage', color: 'text-blue-600' },
    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/votreprofil', color: 'text-pink-500' },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, placeholder: 'https://wa.me/221770000000', color: 'text-green-500' },
    { key: 'telegram', label: 'Telegram', icon: Telegram, placeholder: 'https://t.me/votregroupe', color: 'text-blue-400' }
  ];

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'social_links'));
      if (settingsDoc.exists()) {
        setLinks(settingsDoc.data());
      }
    } catch (error) {
      console.error('Error fetching social links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'settings', 'social_links'), links);
      toast.success('Liens mis à jour avec succès');
    } catch (error) {
      // If document doesn't exist, create it
      try {
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'settings', 'social_links'), links);
        toast.success('Liens enregistrés avec succès');
      } catch (err) {
        console.error('Error saving social links:', err);
        toast.error('Erreur lors de l\'enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Si c'est un membre (non admin), on montre une vue "Rejoindre la communauté"
  if (!isAdmin) {
    const availableLinks = socialPlatforms.filter(p => links[p.key]);

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 md:p-12 text-white shadow-2xl">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-20" />
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4">Rejoignez la Communauté</h1>
            <p className="text-slate-400 text-lg md:text-xl">Restez connecté avec le Dahira et ne manquez aucune actualité importante sur nos plateformes officielles.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {socialPlatforms.map((platform) => {
            const url = links[platform.key];
            if (!url) return null;

            return (
              <Card key={platform.key} className="group border-slate-200 dark:border-slate-800 hover:border-primary transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 rounded-[2.5rem] overflow-hidden bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ${platform.color.replace('text-', 'bg-').replace('500', '500/10').replace('600', '600/10').replace('400', '400/10')}`}>
                    <platform.icon className={`h-7 w-7 ${platform.color}`} />
                  </div>
                  <h3 className="text-xl font-black mb-2 tracking-tight">{platform.label}</h3>
                  <p className="text-slate-500 text-xs md:text-sm leading-relaxed mb-8 flex-grow">Retrouvez nos contenus exclusifs et suivez-nous en direct.</p>
                  <Button
                    asChild
                    className="w-full h-12 rounded-xl font-bold bg-slate-900 hover:bg-primary text-white transition-all shadow-lg hover:shadow-primary/25 group/btn"
                  >
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      Rejoindre <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {availableLinks.length === 0 && (
          <div className="py-24 text-center">
            <LinkIcon className="h-16 w-16 mx-auto mb-4 text-slate-200" />
            <p className="text-slate-500 text-lg">Aucun lien n'a encore été configuré par l'administration.</p>
          </div>
        )}
      </div>
    );
  }

  // VUE ADMIN (Existante avec corrections mineures)
  return (
    <div className="space-y-6" data-testid="social-links-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Réseaux Sociaux</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Configurez les liens vers vos plateformes sociales</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-2xl shadow-lg shadow-primary/20 w-full sm:w-auto"
          data-testid="save-social-links-btn"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Enregistrer les liens</>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Edit Links */}
        <Card className="border-border/50 rounded-[2rem] shadow-xl overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-primary" />
              Configuration des liens
            </CardTitle>
            <CardDescription>
              Entrez les URLs complètes de vos réseaux sociaux
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8 space-y-6">
            {socialPlatforms.map((platform) => (
              <div key={platform.key} className="space-y-2">
                <Label htmlFor={platform.key} className="flex items-center gap-2 font-bold text-slate-700">
                  <platform.icon className={`h-4 w-4 ${platform.color}`} />
                  {platform.label}
                </Label>
                <Input
                  id={platform.key}
                  value={links[platform.key] || ''}
                  onChange={(e) => setLinks({ ...links, [platform.key]: e.target.value })}
                  placeholder={platform.placeholder}
                  className="h-12 rounded-xl border-slate-200 focus:ring-primary/20"
                  data-testid={`social-input-${platform.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="border-border/50 rounded-[2rem] shadow-xl bg-slate-900 text-white overflow-hidden">
          <CardHeader>
            <CardTitle>Aperçu en direct</CardTitle>
            <CardDescription className="text-slate-400">
              Voici comment les membres verront cette page
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid grid-cols-1 gap-4">
              {socialPlatforms.map((platform) => {
                const url = links[platform.key];
                return url ? (
                  <div
                    key={platform.key}
                    className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${platform.color.replace('text-', 'bg-').replace('500', '500/10').replace('600', '600/10').replace('400', '400/10')}`}>
                        <platform.icon className={`h-5 w-5 ${platform.color}`} />
                      </div>
                      <span className="font-bold">{platform.label}</span>
                    </div>
                    <ExternalLink className="h-4 w-4 text-slate-500" />
                  </div>
                ) : null;
              })}

              {!Object.values(links).some(Boolean) && (
                <div className="text-center py-12">
                  <p className="text-slate-500 text-sm">Aucun lien configuré</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
